import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Custom keyframes for glow
const successGlow = {
  boxShadow: [
    '0 0 0 0 rgba(34,197,94,0.4)',
    '0 0 0 8px rgba(34,197,94,0.2)',
    '0 0 0 0 rgba(34,197,94,0.4)'
  ]
};

export default function SeaWolfResults({ result, loading, onSolveNext }) {
  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        className="py-16 flex justify-center"
      >
        <div className="relative w-40 h-40">
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{ background: 'conic-gradient(#3B82F6 0deg 200deg, transparent 200deg 360deg)' }}
            animate={{ rotate: 360 }}
            transition={{ duration: 2, ease: 'linear', repeat: Infinity }}
          />
          <div className="absolute inset-3 rounded-full bg-gray-900 flex items-center justify-center">
            <span className="text-blue-300 font-medium">Solving…</span>
          </div>
        </div>
      </motion.div>
    );
  }

  // Defensive: handle missing result or solution
  if (!result || !result.solution) {
    return (
      <div className="text-center text-blue-300 py-8 font-semibold">
        No results available. Please solve the microbe selection first.
      </div>
    );
  }

  // Extract fields from API output
  const { siteNumber } = result;
  const {
    triple = [],
    score,
    buffer,
    scenario,
    averages = {}
  } = result.solution;

  // Score pill
  const isPerfect = score === 100;
  const pill = isPerfect
    ? { text: '✨ 100% Perfect', cls: 'bg-green-900/20 border-green-500/30 text-green-300' }
    : { text: `${score}% Match`, cls: 'bg-blue-900/20 border-blue-500/30 text-blue-300' };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="seawolf-results-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="mx-auto w-full max-w-2xl rounded-2xl overflow-hidden border border-blue-800/30 bg-gradient-to-br from-gray-950/80 to-blue-950/60 shadow-xl"
        style={isPerfect ? { boxShadow: '0 0 32px 0 rgba(34,197,94,0.25), 0 8px 32px rgba(0,0,0,0.3)' } : {}}
      >
        {/* header */}
        <div className="flex items-center justify-between px-3 md:px-6 py-3 md:py-4 bg-gradient-to-r from-gray-900 to-blue-900">
          <div className="flex items-center gap-2 md:gap-3">
            <span className="text-xl md:text-2xl">🦠</span>
            <h2 className="text-white font-semibold text-base md:text-lg tracking-wide">Sea Wolf Results</h2>
            <span className="text-blue-400 text-sm md:text-base ml-1 md:ml-2">Site {siteNumber || '?'}</span>
          </div>
          <motion.span
            className={`px-2 md:px-3 py-1 rounded-lg text-xs md:text-sm font-medium border ${pill.cls} ${isPerfect ? 'animate-pulse' : ''}`}
            animate={isPerfect ? successGlow : {}}
            transition={isPerfect ? { duration: 1.5, repeat: Infinity, repeatType: 'loop' } : {}}
            style={isPerfect ? { filter: 'drop-shadow(0 0 8px #22c55e88)' } : {}}
          >
            {pill.text}
          </motion.span>
        </div>

        {/* body */}
        <div className="p-4 md:p-8 space-y-6 md:space-y-8">
          {/* scenario */}
          <div className="flex items-center gap-2 md:gap-3 text-blue-400 text-sm md:text-base font-medium">
            <span>🌊</span>
            <span>Scenario</span>
            <span className="text-blue-100 font-normal">{scenario}</span>
          </div>

          {/* best microbes */}
          <div className="bg-blue-900/30 border border-blue-500/20 rounded-lg p-3 md:p-4 space-y-2 md:space-y-3">
            <div className="flex items-center gap-2 text-blue-100 font-medium mb-1 md:mb-2 text-sm md:text-base">
              <span>🧬</span>
              Best Microbes to Choose
            </div>
            <div className="space-y-2 md:space-y-3">
              {triple.map((t, i) => (
                <div 
                  key={t.id} 
                  className="transform transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:shadow-blue-500/10"
                >
                  <div className="flex items-center gap-2 bg-blue-800/60 border border-blue-700/40 rounded-lg p-2 md:p-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-md bg-gray-800 text-gray-300 text-sm md:text-base font-semibold">
                      {i + 1}
                    </span>
                    <span className="text-blue-200 text-sm md:text-base font-mono">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* averages */}
          <div className="bg-blue-900/30 border border-blue-500/20 rounded-lg p-3 md:p-4 space-y-2 md:space-y-3">
            <div className="flex items-center gap-2 text-blue-100 font-medium mb-1 md:mb-2 text-sm md:text-base">
              <span>📊</span>
              Attribute Averages
            </div>
            <div className="space-y-2 md:space-y-3">
              {Object.entries(averages).map(([attr, value], i) => (
                <div 
                  key={attr}
                  className="transform transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:shadow-blue-500/10"
                >
                  <div className="flex items-center gap-2 bg-blue-800/60 border border-blue-700/40 rounded-lg p-2 md:p-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-md bg-gray-800 text-gray-300 text-sm md:text-base font-semibold">
                      {i + 1}
                    </span>
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-blue-200 font-mono text-sm md:text-base">{attr}</span>
                      <span className="text-blue-300 font-medium text-sm md:text-base">{Number(value).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* buffer */}
          <div className="flex items-center gap-2 md:gap-3 text-blue-400 text-sm md:text-base font-medium">
            <span>🛡️</span>
            <span>Buffer (margin)</span>
            <span className="text-blue-100 font-normal">{buffer !== undefined ? Number(buffer).toFixed(2) : '?'}</span>
          </div>

          {/* copy instruction and next site button */}
          <div className="mt-4 md:mt-6 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0">
            <div className="text-green-300 font-semibold flex items-center gap-1 md:gap-2 text-base md:text-lg">
              <span className="text-xl md:text-2xl">✔</span>
              <span>Copy these three microbes into the game.</span>
            </div>
            <button
              onClick={onSolveNext}
              className="w-full md:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg 
                transition-colors duration-200 flex items-center justify-center md:justify-start gap-2 shadow-lg shadow-blue-500/20
                hover:shadow-blue-500/30 transform hover:scale-[1.02]"
            >
              <span>Solve Next Site</span>
              <span>→</span>
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
} 